import { IonButton, IonContent, IonPage, IonSpinner } from '@ionic/react';
import { type ReactNode, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import type { Company } from '~/api/auth';
import {
  type CompanyUser,
  type GrantedLevels,
  companyTeamRefusalOf,
  grantedLevelsOf,
} from '~/api/companyUsers';
import { useCompany, useModuleLevel } from '~/api/hooks/useCompany';
import { useCompanyUsersQuery, useUpdateCompanyUser } from '~/api/hooks/useCompanyUsers';
import { AccessLevelsPicker } from '~/components/AccessLevelsPicker';
import { Alert } from '~/components/Alert';
import { CompanyRolePicker } from '~/components/CompanyRolePicker';
import { Icon } from '~/components/ui/Icon';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { useNetworkStatus } from '~/hooks/useNetworkStatus';
import { ROUTES } from '~/routes';
import { accessLevelFieldErrors } from '~/schemas/companyUser';
import {
  ACCESS_LEVEL,
  type AccessLevel,
  type CompanyRole,
  type FiscalModuleKey,
  atLeast,
} from '~/session/access';
import { useAuthStore } from '~/stores/auth';

const GONE_NOTICE = 'Este acesso não está mais nesta empresa.';
const SELF_NOTICE = 'Você não muda o seu próprio acesso.';
const OFFLINE_NOTICE = 'Sem conexão. Conecte-se para salvar.';
const FIELD_LABEL_CLASS = 'font-mono text-[11px] uppercase tracking-overline text-ink-muted';

type LevelErrors = Partial<Record<FiscalModuleKey, string>>;

export function EditarUsuario(): JSX.Element {
  const { selected } = useCompany();
  const { id } = useParams<{ id: string }>();

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen px-6 pb-8 pt-3">
          {selected ? (
            <MemberAccess company={selected} linkId={Number(id)} />
          ) : (
            <Redirect to={ROUTES.dashboard} />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

interface MemberAccessProps {
  company: Company;
  linkId: number;
}

/**
 * O vínculo vem da lista da equipe, a mesma consulta da tela anterior: assim a tela abre na
 * hora, funciona com o cache do aparelho e não há uma segunda verdade sobre o mesmo dado. Sem
 * `company_users` `FULL` na empresa em operação a consulta nem sai: o portão não fica só no
 * `RequireModule`.
 */
function MemberAccess({ company, linkId }: MemberAccessProps): JSX.Element {
  const nav = useAppNavigation();
  const canManageTeam = atLeast(useModuleLevel('company_users'), ACCESS_LEVEL.full);
  const membersQuery = useCompanyUsersQuery(company.id, { enabled: canManageTeam });
  const member = membersQuery.data?.find((row) => row.id === linkId);

  if (membersQuery.isPending) {
    return (
      <MemberScreen title="Acesso na empresa" onBack={() => nav.back()}>
        <output className="mt-8 flex items-center gap-2">
          <IonSpinner name="dots" aria-hidden="true" />
          <span className="sr-only">Carregando o acesso</span>
        </output>
      </MemberScreen>
    );
  }

  if (!member) {
    return (
      <MemberScreen title="Acesso na empresa" onBack={() => nav.back()}>
        <div className="mt-6">
          <Alert message={GONE_NOTICE} />
          <IonButton
            expand="block"
            fill="outline"
            className="mt-4"
            onClick={() => nav.replace(ROUTES.empresaUsuarios)}
          >
            Ver a lista
          </IonButton>
        </div>
      </MemberScreen>
    );
  }

  return <MemberForm company={company} member={member} />;
}

interface MemberFormProps {
  company: Company;
  member: CompanyUser;
}

function MemberForm({ company, member }: MemberFormProps): JSX.Element {
  const nav = useAppNavigation();
  const { isOnline } = useNetworkStatus();
  const signedInUserId = useAuthStore((state) => state.user?.id);
  const update = useUpdateCompanyUser(company.id);

  const [role, setRole] = useState<CompanyRole>(member.role);
  const [levels, setLevels] = useState<GrantedLevels>(grantedLevelsOf(member.access));
  const [levelErrors, setLevelErrors] = useState<LevelErrors>({});
  const [roleError, setRoleError] = useState<string | undefined>(undefined);
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);

  const isSelf = member.user.id === signedInUserId;

  const changeLevel = (module: FiscalModuleKey, level: AccessLevel): void => {
    setLevels((current) => ({ ...current, [module]: level }));
  };

  const showRefusal = (error: Error): void => {
    const refusal = companyTeamRefusalOf(error);
    setRefusalMessage(refusal.message || null);
    setRoleError(refusal.fieldErrors.role);
    setLevelErrors(accessLevelFieldErrors(refusal.fieldErrors));
  };

  const save = (): void => {
    if (!isOnline) return;
    setRefusalMessage(null);
    setRoleError(undefined);
    setLevelErrors({});
    update.mutate(
      { id: member.id, body: { role, access: levels } },
      {
        onSuccess: () => nav.replace(ROUTES.empresaUsuarios),
        onError: showRefusal,
      },
    );
  };

  const toggleActive = (): void => {
    if (!isOnline) return;
    setRefusalMessage(null);
    update.mutate(
      { id: member.id, body: { is_active: !member.is_active } },
      { onError: showRefusal },
    );
  };

  return (
    <MemberScreen
      title={member.user.full_name}
      subtitle={member.user.email}
      onBack={() => nav.back()}
      tag={!member.is_active ? 'Inativo' : undefined}
    >
      <div className="mt-6 flex flex-col gap-6">
        <CompanyRolePicker value={role} onChange={setRole} error={roleError} disabled={isSelf} />

        <div>
          <p className={FIELD_LABEL_CLASS}>Acesso por módulo</p>
          <div className="mt-3">
            <AccessLevelsPicker
              value={levels}
              ceiling={company.access}
              onChange={changeLevel}
              errors={levelErrors}
              disabled={isSelf}
            />
          </div>
        </div>
      </div>

      {refusalMessage && (
        <div className="mt-6">
          <Alert message={refusalMessage} />
        </div>
      )}

      {isSelf ? (
        <p className="mt-6 text-[13px] text-ink-muted">{SELF_NOTICE}</p>
      ) : (
        <>
          <IonButton
            expand="block"
            className="rx-button mt-6"
            disabled={update.isPending || !isOnline}
            onClick={save}
          >
            {update.isPending ? <IonSpinner name="dots" /> : 'Salvar'}
          </IonButton>
          <IonButton
            expand="block"
            fill="outline"
            className="mt-3"
            disabled={update.isPending || !isOnline}
            onClick={toggleActive}
          >
            {member.is_active ? 'Desativar' : 'Reativar'}
          </IonButton>
          {!isOnline && <p className="mt-2 text-[12px] text-ink-subtle">{OFFLINE_NOTICE}</p>}
          <p className="mt-4 text-[12px] text-ink-subtle">
            Nome, e-mail e senha são da conta de quem entra; só o titular os muda.
          </p>
        </>
      )}
    </MemberScreen>
  );
}

interface MemberScreenProps {
  title: string;
  subtitle?: string;
  tag?: string;
  onBack(): void;
  children: ReactNode;
}

function MemberScreen({ title, subtitle, tag, onBack, children }: MemberScreenProps): JSX.Element {
  return (
    <>
      <button type="button" className="rx-iconbtn" onClick={onBack} aria-label="Voltar">
        <Icon name="back" size={18} />
      </button>
      <div className="mt-3.5 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[26px] font-medium tracking-display-tight text-ink">{title}</h1>
          {subtitle && <p className="mt-1 truncate text-[13px] text-ink-muted">{subtitle}</p>}
        </div>
        {tag && <span className="rx-tag rx-tag-warn">{tag}</span>}
      </div>
      {children}
    </>
  );
}
